import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";

export type EntityType = "lead" | "child";

export interface VisibleStatusEntry {
  statusId: number;
  visible: boolean;
}

interface Args {
  entityType: EntityType | null | undefined;
  entityId: number | null | undefined;
  stageId: number | null | undefined;
  enabled?: boolean;
}

/**
 * Fetches the per-status visibility flags for a given lead or child in a
 * given stage. Empty `statusPreferences` on the server side → always visible
 * (the resolver returns `visible: true`), so a status with no rules behaves
 * exactly as it does today.
 *
 * Failure mode: on error or while loading, the consumer should treat all
 * statuses as visible (fail-open) — preferences are a convenience filter,
 * not a security boundary, and a hidden status would be unrecoverable from
 * the UI.
 */
export default function useVisibleStatuses({
  entityType,
  entityId,
  stageId,
  enabled = true,
}: Args) {
  const ready =
    enabled &&
    !!entityType &&
    Number.isFinite(entityId as number) &&
    (entityId as number) > 0 &&
    Number.isFinite(stageId as number) &&
    (stageId as number) > 0;

  const query = useQuery({
    queryKey: ["visible-statuses", entityType, entityId, stageId],
    queryFn: async () => {
      const params = new URLSearchParams({
        entityType: String(entityType),
        entityId: String(entityId),
        stageId: String(stageId),
      });
      const res = await axiosInstance.get(
        `/core/pipeline/status/visible?${params.toString()}`,
      );
      return (res.data?.data ?? []) as VisibleStatusEntry[];
    },
    enabled: ready,
    // Status visibility is admin-configured (not user data) and rarely
    // changes mid-session; the key already re-fetches on stageId change.
    // 30s meant every open→close→reopen of a lead re-hit the endpoint.
    staleTime: 3 * 60 * 1000,
  });

  // Build an O(1) lookup. We map *all* known statuses to true by default and
  // only mark a status hidden when the resolver explicitly says so —
  // mirrors the fail-open contract.
  const visibleSet = new Set<number>();
  const hiddenSet = new Set<number>();
  (query.data ?? []).forEach((row) => {
    if (row.visible) visibleSet.add(row.statusId);
    else hiddenSet.add(row.statusId);
  });

  const isVisible = (statusId: number | string | null | undefined): boolean => {
    if (statusId == null) return true;
    const id = Number(statusId);
    if (!Number.isFinite(id)) return true;
    // Loading or errored → fail-open. If the resolver hasn't told us about a
    // status yet, default to visible.
    if (query.isLoading || query.isError || !query.data) return true;
    return !hiddenSet.has(id);
  };

  return {
    isVisible,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
